// IndependentMediaUpload.jsx
import React, {
  forwardRef,
  useState,
  useEffect,
  useImperativeHandle,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import { FaPlus, FaTrashAlt, FaGripVertical, FaCut, FaVolumeUp, FaVolumeMute, FaSpinner } from "react-icons/fa";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { uploadFile } from "../../firebase/fileUpload";
import CarouselMediaConfig from "../../components/eventAdditionalData/child/CarouselMediaConfig";
import { captionLengthTone } from "../../helpers/captionBudget";

// ── MediaData model ──────────────────────────────────────────
class MediaData {
  constructor(
    label = "",
    url = "",
    description = "",
    duration = 0,
    mandatory = false,
    defaultValue = false,
    dontMute = false,
    sequence = 0,
    trimStart = 0,
    trimEnd = 0
  ) {
    this.label = label;
    this.url = url;
    this.description = description;
    this.duration = duration;
    this.mandatory = mandatory;
    this.default = defaultValue;
    this.dontMute = dontMute;
    this.sequence = sequence;
    this.trimStart = trimStart;
    this.trimEnd = trimEnd;
  }
}

// ── Video Trim Modal ─────────────────────────────────────────
const VideoTrimModal = ({ videoUrl, trimStart, trimEnd, onSave, onClose }) => {
  const videoRef = useRef(null);
  const [start, setStart] = useState(trimStart || 0);
  const [end, setEnd] = useState(trimEnd || 0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [error, setError] = useState("");
  const MAX_DURATION = 300; // 5 minutes in seconds

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      const dur = videoRef.current.duration;
      setVideoDuration(dur);
      if (end === 0 || end > dur) {
        setEnd(Math.min(dur, MAX_DURATION));
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      // Stop at end marker
      if (videoRef.current.currentTime >= end) {
        videoRef.current.pause();
      }
    }
  };

  const handleStartChange = (val) => {
    const v = parseFloat(val) || 0;
    if (v >= end) {
      setError("Start must be before end");
      return;
    }
    setError("");
    setStart(v);
    if (videoRef.current) videoRef.current.currentTime = v;
  };

  const handleEndChange = (val) => {
    const v = parseFloat(val) || 0;
    if (v <= start) {
      setError("End must be after start");
      return;
    }
    if (v - start > MAX_DURATION) {
      setError(`Max trim duration is ${MAX_DURATION / 60} minutes`);
      return;
    }
    setError("");
    setEnd(v);
  };

  const previewTrim = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (videoRef.current) {
      videoRef.current.currentTime = start;
      videoRef.current.play();
    }
  };

  const trimDuration = (end - start).toFixed(1);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-800">✂️ Trim Video</h3>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
          </div>

          <video
            ref={videoRef}
            src={videoUrl}
            controls
            onLoadedMetadata={handleLoadedMetadata}
            onTimeUpdate={handleTimeUpdate}
            className="w-full rounded-lg mb-4 max-h-64 bg-black"
          />

          {/* Range slider visual */}
          <div className="mb-4">
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute h-full bg-blue-200"
                style={{
                  left: `${(start / (videoDuration || 1)) * 100}%`,
                  width: `${((end - start) / (videoDuration || 1)) * 100}%`,
                }}
              />
              <div
                className="absolute h-full w-1 bg-red-500"
                style={{ left: `${(currentTime / (videoDuration || 1)) * 100}%` }}
              />
            </div>
            <div className="flex justify-between text-xs text-gray-500 mt-1">
              <span>0s</span>
              <span>{videoDuration.toFixed(1)}s</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start (secs)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max={videoDuration}
                value={start}
                onChange={(e) => handleStartChange(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End (secs)</label>
              <input
                type="number"
                step="0.1"
                min="0"
                max={videoDuration}
                value={end}
                onChange={(e) => handleEndChange(e.target.value)}
                className="border rounded px-3 py-2 w-full"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mb-4">
            <div className="text-sm text-gray-600">
              Trim duration: <span className="font-semibold text-blue-600">{trimDuration}s</span>
              {trimDuration > MAX_DURATION && (
                <span className="text-red-500 ml-2">(max {MAX_DURATION / 60} min)</span>
              )}
            </div>
            <button
              type="button"
              onClick={previewTrim}
              className="text-sm bg-gray-100 hover:bg-gray-200 px-3 py-1.5 rounded transition"
            >
              ▶ Preview
            </button>
          </div>

          {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                if (end - start > MAX_DURATION) {
                  setError(`Max trim duration is ${MAX_DURATION / 60} minutes`);
                  return;
                }
                onSave(parseFloat(start.toFixed(1)), parseFloat(end.toFixed(1)));
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              Save Trim
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Reorder helper ───────────────────────────────────────────
const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  // Re-assign sequence numbers after reorder
  return result.map((item, idx) => ({ ...item, sequence: idx }));
};

/**
 * Props:
 * - eventId: string | null (edit flow has id; create flow null)
 * - draft: { imageData, videoData, musicData, videoOutputDuration, musicDuration }
 * - onDraftChange: fn(draft)
 */
const IndependentMediaUpload = forwardRef(({ eventId, draft, onDraftChange, ppCaption2 = "", onPpCaption2Change = null, isPostCarousel = false, setIsPostCarousel = () => {}, postCarouselConfig = { adminMediaCount: 0, userMediaCount: 0, mediaItems: [] }, setPostCarouselConfig = () => {}, frameImageUrl = null, onFrameImageUrlChange = null }, ref) => {
  const apiDomain = process.env.REACT_APP_API_DOMAIN;
  const [framePreview, setFramePreview] = useState(frameImageUrl || null);
  const [frameUploading, setFrameUploading] = useState(false);

  // Sync frame preview when prop changes (e.g. edit mode hydration)
  useEffect(() => {
    if (frameImageUrl && (!framePreview || !framePreview.startsWith("blob:"))) {
      setFramePreview(frameImageUrl);
    }
  }, [frameImageUrl]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ensure sequence is assigned on init
  const assignSequence = (arr) =>
    (arr || []).map((item, idx) => ({ ...item, sequence: item.sequence ?? idx }));

  const [imageData, setImageData] = useState(() => {
    const d = draft?.imageData;
    return assignSequence(Array.isArray(d) && d.length ? d : [new MediaData()]);
  });
  const [videoData, setVideoData] = useState(() => {
    const d = draft?.videoData;
    return assignSequence(Array.isArray(d) && d.length ? d : [new MediaData()]);
  });
  const [musicData, setMusicData] = useState(() => {
    const d = draft?.musicData;
    return assignSequence(Array.isArray(d) && d.length ? d : [new MediaData()]);
  });
  const [videoOutputDuration, setVideoOutputDuration] = useState(draft?.videoOutputDuration || 0);

  // Trim modal state
  const [trimModal, setTrimModal] = useState({ open: false, index: -1 });

  // Upload progress: key = "type-index", value = 0..100 or null
  const [uploadProgress, setUploadProgress] = useState({});

  // In edit mode we must wait for the API fetch before syncing back to the
  // parent — otherwise the initial placeholder state ([new MediaData()]) would
  // overwrite the pre-fetched mediaDraft and the tab would appear empty when
  // the user switches away before the fetch completes.
  // In create mode (no eventId) there is no fetch, so sync is immediate.
  const syncEnabled = useRef(!eventId);

  // Hydrate from backend only when editing existing event
  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;
    const fetchMediaData = async () => {
      try {
        const response = await axios.get(`${apiDomain}/api/v2/getlongevent`, {
          params: { event_id: eventId },
        });
        if (!cancelled && response.status === 200 && response.data) {
          const data = response.data;
          const images = JSON.parse(data.image_data || "[]");
          const videos = JSON.parse(data.video_data || "[]");
          const music  = JSON.parse(data.music_data || "[]");

          const newImages = assignSequence(Array.isArray(images) && images.length ? images : [new MediaData()]);
          const newVideos = assignSequence(Array.isArray(videos) && videos.length ? videos : [new MediaData()]);
          const newMusic  = assignSequence(Array.isArray(music)  && music.length  ? music  : [new MediaData()]);
          const newDuration = data.video_output_duration_in_secs || 0;

          setImageData(newImages);
          setVideoData(newVideos);
          setMusicData(newMusic);
          setVideoOutputDuration(newDuration);

          // Enable sync and push fetched data to parent immediately so that
          // switching tabs before the next render doesn't leave mediaDraft empty.
          syncEnabled.current = true;
          onDraftChange && onDraftChange({
            imageData: newImages,
            videoData: newVideos,
            musicData: newMusic,
            videoOutputDuration: newDuration,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching media data:", error);
          syncEnabled.current = true; // allow subsequent edits to sync even on error
        }
      }
    };
    fetchMediaData();
    return () => { cancelled = true; };
  }, [apiDomain, eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep parent draft in sync after initial hydration.
  // Skipped while fetch is in-flight to avoid overwriting pre-fetched data.
  useEffect(() => {
    if (!syncEnabled.current) return;
    onDraftChange &&
      onDraftChange({
        imageData,
        videoData,
        musicData,
        videoOutputDuration,
      });
  }, [imageData, videoData, musicData, videoOutputDuration, onDraftChange]);

  // Expose stringified payload (exactly like your working old component)
  useImperativeHandle(ref, () => ({
    getPayload: () => ({
      number_of_images: imageData.length,
      number_of_videos: videoData.length,
      number_of_music: musicData.length,
      image_data: JSON.stringify(imageData),
      video_data: JSON.stringify(videoData),
      music_data: JSON.stringify(musicData),
      video_output_duration_in_secs: parseInt(String(videoOutputDuration), 10) || 0,
      music_duration_in_secs: 0,
    }),
  }));

  // ── File upload ────────────────────────────────────────────
  const handleFileUploadLocal = async (file, type, index) => {
    if (!file) return;

    // Validate video: max 100MB, max 5 min
    if (type === "video") {
      if (file.size > 100 * 1024 * 1024) {
        alert("Video file must be under 100 MB");
        return;
      }
    }

    const progressKey = `${type}-${index}`;
    setUploadProgress((prev) => ({ ...prev, [progressKey]: 0 }));

    const url = await uploadFile(file, (pct) => {
      setUploadProgress((prev) => ({ ...prev, [progressKey]: pct }));
    });

    // Switch to "generating preview" state (cleared when media element loads)
    setUploadProgress((prev) => ({ ...prev, [progressKey]: "preview" }));

    if (!url) {
      // Upload failed – clear progress entirely
      setUploadProgress((prev) => {
        const next = { ...prev };
        delete next[progressKey];
        return next;
      });
      return;
    }

    const update = (list, setter) => {
      const copy = [...list];
      copy[index] = { ...(copy[index] || new MediaData()), url };
      // For video, auto-open trim modal
      if (type === "video") {
        copy[index].trimStart = 0;
        copy[index].trimEnd = 0;
      }
      setter(copy);
    };

    if (type === "image") update(imageData, setImageData);
    if (type === "video") {
      update(videoData, setVideoData);
      // Auto-open trim modal after upload
      setTimeout(() => setTrimModal({ open: true, index }), 500);
    }
    if (type === "music") update(musicData, setMusicData);
  };

  const handleRemove = (index, type) => {
    const update = (list, setter) => {
      const copy = list.filter((_, i) => i !== index).map((item, idx) => ({ ...item, sequence: idx }));
      setter(copy);
    };
    if (type === "image") update(imageData, setImageData);
    if (type === "video") update(videoData, setVideoData);
    if (type === "music") update(musicData, setMusicData);
  };

  const handleMediaChange = (index, value, type, field) => {
    const update = (list, setter) => {
      const copy = [...list];
      copy[index] = { ...(copy[index] || new MediaData()), [field]: value };
      setter(copy);
    };
    if (type === "image") update(imageData, setImageData);
    if (type === "video") update(videoData, setVideoData);
    if (type === "music") update(musicData, setMusicData);
  };

  const handleOverrideToggle = (index, type) => {
    const list = type === "image" ? imageData : type === "video" ? videoData : musicData;
    const setter = type === "image" ? setImageData : type === "video" ? setVideoData : setMusicData;
    const copy = [...list];
    const current = copy[index] || new MediaData();
    copy[index] = { ...current, default: !current.default, mandatory: false };
    setter(copy);
  };

  // ── Don't Mute toggle (video only) ────────────────────────
  const handleDontMuteToggle = (index) => {
    const copy = [...videoData];
    const current = copy[index] || new MediaData();
    copy[index] = { ...current, dontMute: !current.dontMute };
    setVideoData(copy);
  };

  // ── Drag & Drop handlers ──────────────────────────────────
  const onDragEndImages = useCallback((result) => {
    if (!result.destination) return;
    setImageData((prev) => reorder(prev, result.source.index, result.destination.index));
  }, []);

  const onDragEndVideos = useCallback((result) => {
    if (!result.destination) return;
    setVideoData((prev) => reorder(prev, result.source.index, result.destination.index));
  }, []);

  const onDragEndMusic = useCallback((result) => {
    if (!result.destination) return;
    setMusicData((prev) => reorder(prev, result.source.index, result.destination.index));
  }, []);

  // ── Trim save handler ─────────────────────────────────────
  const handleTrimSave = (start, end) => {
    const copy = [...videoData];
    copy[trimModal.index] = {
      ...copy[trimModal.index],
      trimStart: start,
      trimEnd: end,
    };
    setVideoData(copy);
    setTrimModal({ open: false, index: -1 });
  };

  // ── Draggable media card ───────────────────────────────────
  const renderDraggableMediaCard = (media, index, type, provided, snapshot) => (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={`p-4 border rounded-lg shadow-md bg-white relative flex flex-col gap-4 transition-shadow ${
        snapshot.isDragging ? "shadow-xl ring-2 ring-blue-400" : ""
      }`}
    >
      {/* Drag handle */}
      <div
        {...provided.dragHandleProps}
        className="absolute top-2 left-2 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 bg-white rounded p-1 shadow-sm z-10"
        title="Drag to reorder"
      >
        <FaGripVertical />
      </div>

      {/* Sequence badge */}
      <div className="absolute top-2 left-10 bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full z-10">
        #{index + 1}
      </div>

      <button
        type="button"
        className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full hover:bg-red-600 transition z-10"
        onClick={() => handleRemove(index, type)}
      >
        <FaTrashAlt />
      </button>

      <div
        className="relative flex justify-center items-center w-full h-40 border-dashed border-2 rounded-md cursor-pointer hover:border-blue-500 transition mt-6 overflow-hidden"
        onClick={() => {
          if (uploadProgress[`${type}-${index}`] != null) return; // block clicks during upload
          document.getElementById(`${type}-file-input-${index}`)?.click();
        }}
      >
        {media?.url ? (
          type === "image" ? (
            <img
              src={media.url}
              alt="preview"
              className="object-cover w-full h-full rounded-md"
              onLoad={() =>
                setUploadProgress((prev) => {
                  const next = { ...prev };
                  delete next[`${type}-${index}`];
                  return next;
                })
              }
            />
          ) : type === "video" ? (
            <video
              src={media.url}
              controls
              className="object-cover w-full h-full rounded-md"
              onLoadedData={() =>
                setUploadProgress((prev) => {
                  const next = { ...prev };
                  delete next[`${type}-${index}`];
                  return next;
                })
              }
            />
          ) : (
            <audio
              src={media.url}
              controls
              className="w-full"
              onLoadedData={() =>
                setUploadProgress((prev) => {
                  const next = { ...prev };
                  delete next[`${type}-${index}`];
                  return next;
                })
              }
            />
          )
        ) : uploadProgress[`${type}-${index}`] == null ? (
          <div className="text-gray-400 flex flex-col items-center">
            <FaPlus className="text-xl" />
            <span className="text-sm">Add {type}</span>
          </div>
        ) : null}

        {/* Upload progress / generating preview overlay */}
        {uploadProgress[`${type}-${index}`] != null && (
          <div className="absolute inset-0 bg-white/80 flex flex-col items-center justify-center z-20">
            {uploadProgress[`${type}-${index}`] === "preview" ? (
              <>
                <FaSpinner className="animate-spin text-2xl text-blue-600 mb-2" />
                <span className="text-sm font-semibold text-blue-700">Generating preview…</span>
              </>
            ) : (
              <>
                <div className="w-3/4 bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-blue-600 h-full rounded-full transition-all duration-300"
                    style={{ width: `${Math.max(0, uploadProgress[`${type}-${index}`])}%` }}
                  />
                </div>
                <span className="text-sm font-semibold text-blue-700 mt-2">
                  {uploadProgress[`${type}-${index}`] >= 0
                    ? `${Math.round(uploadProgress[`${type}-${index}`])}%`
                    : "Error"}
                </span>
              </>
            )}
          </div>
        )}

        <input
          id={`${type}-file-input-${index}`}
          type="file"
          accept={type === "image" ? "image/*" : type === "video" ? "video/*" : "audio/*"}
          className="hidden"
          onChange={(e) => handleFileUploadLocal(e.target.files?.[0], type, index)}
        />
      </div>

      {/* Trim + Change controls for video */}
      {type === "video" && media?.url && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setTrimModal({ open: true, index })}
            className="flex items-center gap-1 text-sm bg-purple-50 text-purple-700 px-3 py-1.5 rounded-lg hover:bg-purple-100 transition border border-purple-200"
          >
            <FaCut className="text-xs" /> Trim Video
          </button>
          <button
            type="button"
            onClick={() => {
              const input = document.getElementById(`${type}-file-input-${index}`);
              if (input) { input.value = ""; input.click(); }
            }}
            className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition border border-blue-200"
          >
            <FaPlus className="text-xs" /> Change Video
          </button>
          {(media.trimStart > 0 || media.trimEnd > 0) && (
            <span className="text-xs text-gray-500">
              {media.trimStart}s – {media.trimEnd}s
            </span>
          )}
          <span className="text-xs text-gray-400 ml-auto">Max: 5 min / 100 MB</span>
        </div>
      )}

      {/* Change button for image / audio slots */}
      {type !== "video" && media?.url && (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              const input = document.getElementById(`${type}-file-input-${index}`);
              if (input) { input.value = ""; input.click(); }
            }}
            className="flex items-center gap-1 text-sm bg-blue-50 text-blue-700 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition border border-blue-200"
          >
            <FaPlus className="text-xs" /> Change {type.charAt(0).toUpperCase() + type.slice(1)}
          </button>
        </div>
      )}

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Label</label>
        <input
          type="text"
          value={media?.label || ""}
          onChange={(e) => handleMediaChange(index, e.target.value, type, "label")}
          placeholder="Label"
          className="border rounded px-3 py-2 w-full"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
        <textarea
          value={media?.description || ""}
          onChange={(e) => handleMediaChange(index, e.target.value, type, "description")}
          placeholder="Description"
          className="border rounded px-3 py-2 w-full resize-none"
        />
      </div>

      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-1">Duration (secs)</label>
        <input
          type="number"
          min="0"
          value={media?.duration || ""}
          onChange={(e) =>
            handleMediaChange(index, e.target.value === "" ? 0 : parseInt(e.target.value, 10), type, "duration")
          }
          placeholder="0"
          className="border rounded px-3 py-2 w-full"
        />
      </div>

      {/* Override by user toggle */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-gray-700">Override by user</span>
        <button
          type="button"
          role="switch"
          aria-checked={!!media?.default}
          onClick={() => handleOverrideToggle(index, type)}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
            !!media?.default ? "bg-blue-600" : "bg-gray-300"
          }`}
        >
          <span
            className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
              !!media?.default ? "translate-x-6" : "translate-x-1"
            }`}
          />
        </button>
        <span className="text-xs text-gray-500">{!!media?.default ? "ON" : "OFF"}</span>
      </div>

      {/* Don't Mute toggle – VIDEO ONLY */}
      {type === "video" && (
        <div className="flex items-center gap-3 border-t pt-3">
          <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
            {media?.dontMute ? (
              <FaVolumeUp className="text-green-600" />
            ) : (
              <FaVolumeMute className="text-gray-400" />
            )}
            Keep Video Audio
          </span>
          <button
            type="button"
            role="switch"
            aria-checked={!!media?.dontMute}
            onClick={() => handleDontMuteToggle(index)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
              !!media?.dontMute ? "bg-green-600" : "bg-gray-300"
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                !!media?.dontMute ? "translate-x-6" : "translate-x-1"
              }`}
            />
          </button>
          <span className="text-xs text-gray-500">
            {!!media?.dontMute ? "Audio ON during reel" : "Muted in reel"}
          </span>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 border rounded-lg shadow-lg bg-gray-50">
      <h2 className="text-2xl font-semibold text-blue-600 mb-6">Competition Reels</h2>

      {/* Caption for competition reel */}
      <div className="mb-6">
        <label className="text-base font-semibold text-gray-700 mb-1 flex items-center justify-between gap-2">
          <span>Caption for competition reel</span>
          {(() => {
            // Same 1750 budget as the participation post caption — the reel
            // also composes with hashtags + NNexus code on Instagram, so it
            // needs the same headroom (Trishul, 2026-06-04).
            const len = (ppCaption2 || "").length;
            const tone = captionLengthTone(len, 1750);
            const color =
              tone === "red"
                ? "text-red-600 bg-red-50"
                : tone === "yellow"
                ? "text-yellow-700 bg-yellow-50"
                : "text-gray-500 bg-gray-100";
            return (
              <span className={`text-xs font-normal px-2 py-0.5 rounded ${color}`}>
                {len}/1750 chars · leaves room for hashtags + NNexus code
              </span>
            );
          })()}
        </label>
        <textarea
          value={ppCaption2 || ""}
          onChange={(e) =>
            typeof onPpCaption2Change === "function" && onPpCaption2Change(e.target.value)
          }
          placeholder="Enter the caption shown on the competition reel"
          rows={4}
          maxLength={1750}
          className="border rounded px-3 py-2 w-full"
        />
      </div>

      {/* Post Carousel Toggle */}
      <section className="bg-purple-50 p-4 border border-purple-200 rounded-md mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xl">🎠</span>
            <div>
              <label className="text-lg font-semibold text-purple-800">
                Enable Post Carousel
              </label>
              <p className="text-sm text-purple-600">
                Create an Instagram carousel post with multiple media items
              </p>
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={!!isPostCarousel}
              onChange={() => setIsPostCarousel(!isPostCarousel)}
              className="sr-only peer"
            />
            <div className="w-14 h-7 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-purple-600"></div>
          </label>
        </div>
      </section>

      {isPostCarousel ? (
        <div className="mb-4">
          {/* Post Frame Upload — only shown when carousel is enabled */}
          <div className="mb-6">
            <label className="block text-lg font-semibold text-gray-800 mb-2">🖼️ Carousel Feature Frame</label>
            <p className="text-sm text-gray-500 mb-2">
              Upload a transparent PNG frame to overlay on user media slots in the carousel.
            </p>
            <div className="flex items-center gap-4">
              <input
                type="file"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const preview = URL.createObjectURL(file);
                  setFramePreview(preview);
                  try {
                    setFrameUploading(true);
                    const url = await uploadFile(file);
                    if (url && typeof onFrameImageUrlChange === "function") {
                      onFrameImageUrlChange(url);
                    }
                  } catch (err) {
                    console.error("Post Frame upload failed:", err);
                  } finally {
                    setFrameUploading(false);
                  }
                }}
                disabled={frameUploading}
                className="border px-4 py-2 rounded-md bg-white disabled:opacity-50"
              />
              {frameUploading && (
                <span className="inline-flex items-center text-sm text-blue-700 font-medium">
                  <FaSpinner className="mr-2 animate-spin" /> Uploading…
                </span>
              )}
            </div>
            {(framePreview || frameImageUrl) && (
              <img
                src={framePreview || frameImageUrl}
                alt="Carousel Feature Frame Preview"
                className="mt-3 w-48 h-48 object-cover rounded-md border-2 border-purple-300"
              />
            )}
          </div>
          <CarouselMediaConfig
            config={postCarouselConfig}
            onConfigChange={setPostCarouselConfig}
            handleFileUpload={uploadFile}
            configKey="postCarousel"
            isEditMode={!!eventId}
            frameImageUrl={frameImageUrl}
          />
        </div>
      ) : (
        <>
          {/* Drag-and-drop info banner */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
            💡 <strong>Drag & Drop</strong> items to reorder. The sequence set here will be followed during reel video creation in the user app.
          </div>

          {/* ── Images (Drag & Drop) ── */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-4">Images</h3>
        <DragDropContext onDragEnd={onDragEndImages}>
          <Droppable droppableId="images-droppable" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {imageData.map((media, index) => (
                  <Draggable
                    key={`image-${index}-${media.url || index}`}
                    draggableId={`image-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) =>
                      renderDraggableMediaCard(media, index, "image", provided, snapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          onClick={() =>
            setImageData([
              ...imageData,
              { ...new MediaData(), sequence: imageData.length },
            ])
          }
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
        >
          Add Image
        </button>
      </div>

      {/* ── Videos (Drag & Drop + Trim + Don't Mute) ── */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-4">Videos</h3>
        <DragDropContext onDragEnd={onDragEndVideos}>
          <Droppable droppableId="videos-droppable" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {videoData.map((media, index) => (
                  <Draggable
                    key={`video-${index}-${media.url || index}`}
                    draggableId={`video-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) =>
                      renderDraggableMediaCard(media, index, "video", provided, snapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          onClick={() =>
            setVideoData([
              ...videoData,
              { ...new MediaData(), sequence: videoData.length },
            ])
          }
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
        >
          Add Video
        </button>
      </div>

      {/* ── Music (Drag & Drop) ── */}
      <div className="mb-8">
        <h3 className="text-lg font-bold mb-4">Music</h3>
        <DragDropContext onDragEnd={onDragEndMusic}>
          <Droppable droppableId="music-droppable" direction="horizontal">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {musicData.map((media, index) => (
                  <Draggable
                    key={`music-${index}-${media.url || index}`}
                    draggableId={`music-${index}`}
                    index={index}
                  >
                    {(provided, snapshot) =>
                      renderDraggableMediaCard(media, index, "music", provided, snapshot)
                    }
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <button
          type="button"
          onClick={() =>
            setMusicData([
              ...musicData,
              { ...new MediaData(), sequence: musicData.length },
            ])
          }
          className="mt-4 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition"
        >
          Add Music
        </button>
      </div>

      {/* Video Output Duration */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700">
          Video Output Duration (secs)
        </label>
        <input
          type="number"
          value={videoOutputDuration}
          onChange={(e) => setVideoOutputDuration(parseInt(e.target.value || "0", 10))}
          className="border rounded px-3 py-2 w-full lg:w-1/2"
        />
      </div>
      </>
      )}

      {/* Trim Modal */}
      {trimModal.open && videoData[trimModal.index]?.url && (
        <VideoTrimModal
          videoUrl={videoData[trimModal.index].url}
          trimStart={videoData[trimModal.index].trimStart || 0}
          trimEnd={videoData[trimModal.index].trimEnd || 0}
          onSave={handleTrimSave}
          onClose={() => setTrimModal({ open: false, index: -1 })}
        />
      )}
    </div>
  );
});

export default IndependentMediaUpload;

